import * as React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '../cn';
import { Badge } from '../Badge';

export interface UnavailableStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  message?: React.ReactNode;
  /** Phase/label chip, e.g. "Pending GIISDEC validation", "Phase II". */
  phase?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

/**
 * EDS governed Unavailable / pending state — Design Bible §7.5/§10.
 * For capabilities that are declared-but-deferred or awaiting data/validation.
 * Phase-tagged and honest — never "broken", never fabricated data.
 */
export const UnavailableState: React.FC<UnavailableStateProps> = ({
  title = 'Temporarily unavailable',
  message = 'This section is being prepared and will appear once it is available.',
  phase,
  icon,
  action,
  className,
  ...props
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-3 rounded-eds-md border border-eds-border bg-eds-surface-2 px-6 py-12 text-center',
      className,
    )}
    {...props}
  >
    <span className="flex h-11 w-11 items-center justify-center rounded-eds-pill bg-eds-muted text-eds-muted-fg" aria-hidden="true">
      {icon ?? <Clock className="h-5 w-5" />}
    </span>
    <div>
      <p className="font-eds-display text-base font-semibold text-eds-fg">{title}</p>
      {message && <p className="mt-1 max-w-sm font-eds-body text-sm text-eds-muted-fg">{message}</p>}
    </div>
    {phase && <Badge tone="gold" dot>{phase}</Badge>}
    {action && <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div>}
  </div>
);

export default UnavailableState;
